import { useEffect, useRef, useMemo } from 'react';
import styles from './CosmicEffects.module.css';

// 漂浮星尘 - 用 useMemo 避免重复生成
export function StarDust({ count = 20 }) {
  const particles = useMemo(() => 
    Array.from({ length: count }).map((_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: 1 + Math.random() * 2,
      duration: 20 + Math.random() * 15,
      delay: Math.random() * 12,
      type: Math.random() > 0.75 ? 'bright' : 'normal',
    })), [count]);

  return (
    <div className={styles.dustContainer}>
      {particles.map((p) => (
        <div
          key={p.id}
          className={`${styles.dustParticle} ${p.type === 'bright' ? styles.bright : ''}`}
          style={{
            left: `${p.x}%`,
            top: `${p.y}%`,
            width: `${p.size}px`,
            height: `${p.size}px`,
            animationDuration: `${p.duration}s`,
            animationDelay: `${p.delay}s`,
          }}
        />
      ))}
    </div>
  );
}

// 星座连线 - 动画完成后停止 requestAnimationFrame
export function ConstellationLines() {
  const canvasRef = useRef(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    canvas.width = canvas.offsetWidth * 2;
    canvas.height = canvas.offsetHeight * 2;
    ctx.scale(2, 2);
    
    const width = canvas.offsetWidth;
    const height = canvas.offsetHeight;
    const cx = width * 0.38;
    const cy = height * 0.5;
    
    // 固定随机种子，避免每次渲染不同
    const stars = Array.from({ length: 8 }).map((_, i) => {
      const angle = (i / 8) * Math.PI * 2 - Math.PI / 2;
      const seed = (i * 137.5) % 1;
      const r = Math.min(width, height) * (0.2 + seed * 0.1);
      return {
        x: cx + Math.cos(angle) * r,
        y: cy + Math.sin(angle) * r,
        size: 1.5 + seed * 1,
        brightness: 0.6 + seed * 0.4,
      };
    });
    
    const connections = [[0,1],[1,2],[2,3],[3,4],[4,5],[5,6],[6,7],[7,0],[0,4],[2,6]];
    let progress = 0;
    let animationId;
    let lastTime = 0;
    const fps = 30; // 限制帧率
    const interval = 1000 / fps;
    
    const draw = (timestamp) => {
      if (timestamp - lastTime < interval) {
        animationId = requestAnimationFrame(draw);
        return;
      }
      lastTime = timestamp;
      
      ctx.clearRect(0, 0, width, height);
      
      // 绘制连线
      connections.forEach(([i, j], idx) => {
        const lineProgress = Math.max(0, Math.min(1, (progress * 1.8 - idx * 0.1)));
        if (lineProgress <= 0) return;
        
        const start = stars[i];
        const end = stars[j];
        const currentEnd = {
          x: start.x + (end.x - start.x) * lineProgress,
          y: start.y + (end.y - start.y) * lineProgress,
        };
        
        ctx.beginPath();
        ctx.moveTo(start.x, start.y);
        ctx.lineTo(currentEnd.x, currentEnd.y);
        ctx.strokeStyle = `rgba(140, 130, 180, ${0.15 * lineProgress})`;
        ctx.lineWidth = 1;
        ctx.stroke();
      });
      
      // 绘制星点
      stars.forEach((star, i) => {
        const appear = Math.min(1, progress * 3 - i * 0.12);
        if (appear <= 0) return;
        
        const size = star.size * appear;
        
        // 简化光晕
        ctx.beginPath();
        ctx.arc(star.x, star.y, size * 4, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(180, 170, 210, ${0.15 * appear * star.brightness})`;
        ctx.fill();
        
        // 核心
        ctx.beginPath();
        ctx.arc(star.x, star.y, size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(240, 238, 250, ${0.85 * appear})`;
        ctx.fill();
      });
      
      if (progress < 1) {
        progress += 0.012;
        animationId = requestAnimationFrame(draw);
      }
      // 动画完成后不再继续 requestAnimationFrame
    };
    
    animationId = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animationId);
  }, []);
  
  return <canvas ref={canvasRef} className={styles.constellationCanvas} />;
}

// 多层光环 - 纯 CSS，无性能问题
export function AuroraRings() {
  return (
    <div className={styles.auroraContainer}>
      <div className={styles.auroraRing} />
      <div className={styles.auroraRing} data-index="2" />
      <div className={styles.centerGlow} />
    </div>
  );
}

// 装饰性网格
export function GridOverlay() {
  return <div className={styles.gridOverlay} />;
}

// 边角装饰
export function CornerDecorations() {
  return (
    <>
      <div className={`${styles.corner} ${styles.topLeft}`} />
      <div className={`${styles.corner} ${styles.topRight}`} />
      <div className={`${styles.corner} ${styles.bottomLeft}`} />
      <div className={`${styles.corner} ${styles.bottomRight}`} />
    </>
  );
}
